import { Filter, FilterConfig, FilterTypes } from "@/lib/filterable";
import videoService from "@/services/mrfiktiv/services/video.service";
import {
  MrfiktivMuxAssetViewModelGen,
  MrfiktivMuxUploadViewModelGen,
  MrfiktivVideoViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { VideoDataAccessLayer } from "@/store/modules/access-layers/video.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { ITimestamp, Timestamp } from "./timestamp.entity";

class VideoBase implements MrfiktivVideoViewModelGen {
  /** The unique identifier of the video */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.Video.id"
  })
  id: string;

  partnerId: string;

  environment: string;

  token?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: item => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId?: string | undefined;

  upload?: MrfiktivMuxUploadViewModelGen | undefined;
  asset?: MrfiktivMuxAssetViewModelGen | undefined;

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  get hasAsset() {
    return !!(this && this.asset && Array.isArray(this.asset.playback_ids) && this.asset.playback_ids.length > 0);
  }

  get src() {
    if (!this.playbackId) {
      return undefined;
    }

    let src = `https://stream.mux.com/${this.playbackId}.m3u8`;

    if (this.token) {
      src += `?token=${this.token}`;
    }

    return src;
  }

  get videoJsSrc() {
    if (!this.playbackId) {
      return undefined;
    }

    let src = this.playbackId;

    if (this.token) {
      src += `?token=${this.token}`;
    }

    return src;
  }

  get playbackId() {
    if (!this.hasAsset) return "";

    if (!this.asset?.playback_ids) {
      return undefined;
    }

    return this.asset?.playback_ids[0]?.id;
  }

  get uploadUrl() {
    return this.upload?.url;
  }

  constructor(video: Partial<MrfiktivVideoViewModelGen>) {
    this.id = video?.id ?? "";
    this.partnerId = video?.partnerId ?? "";
    this.environment = video?.environment ?? "";
    this.userId = video?.userId;
    this.upload = video?.upload;
    this.asset = video?.asset;
    this.token = video?.token;
    this.timestamp = new Timestamp(video?.timestamp);
  }

  map(video?: MrfiktivVideoViewModelGen) {
    this.id = video?.id ?? "";
    this.partnerId = video?.partnerId ?? "";
    this.environment = video?.environment ?? "";
    this.userId = video?.userId ?? "";
    this.upload = video?.upload;
    this.asset = video?.asset;
    this.token = video?.token;

    this.timestamp = new Timestamp(video?.timestamp);
  }

  async fetch(): Promise<this> {
    const res = await videoService.getOne(this.partnerId, this.id);

    this.map(res);

    VideoDataAccessLayer.set(this);

    return this;
  }

  async delete(): Promise<this> {
    await videoService.delete(this.partnerId, this.id);

    VideoDataAccessLayer.delete(this);

    return this;
  }

  async cancel(): Promise<this> {
    const res = await videoService.cancel(this.partnerId, this.id);

    this.map(res);

    return this;
  }
}

type IVideo = VideoBase;
const Video = Filter.createForClass(VideoBase);

export { IVideo, Video };
